import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, TextField, Button, Typography, Container, Paper, Grid, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import Logo from './Logo';


function Login({ setIsAuthenticated, setUserRole, isAuthenticated, userRole, setAgreementSigned }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      if (userRole === 'inactive') {
        navigate('/change-password'); // Redirect inactive users
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, userRole, navigate]);

  const handleLogin = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (data.message === 'Login successful') {
        setIsAuthenticated(true);
        setUserRole(data.user.role); // Set user role
        setAgreementSigned(data.user.agreement_signed || false);
        if (data.user.status === 'inactive') {
          navigate('/change-password'); // Redirect inactive users
        } else if (!data.user.agreement_signed) {
          navigate('/privacy-consent'); // Redirect to privacy consent
        } else {
          navigate('/dashboard'); // Redirect active users
        }
      } else {
  setErrorMessage(t('login_invalid'));
      }
    } catch (error) {
      console.error('Error logging in:', error);
  setErrorMessage(t('login_error'));
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: 6 }, px: { xs: 2, sm: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper elevation={6} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: '15px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, maxWidth: '100%' }}>
            <Logo width={isXs ? 240 : 420} />
          </Box>
          <Typography variant="h6" align="center" gutterBottom>
            {t('login_title')}
          </Typography>
          <form onSubmit={(e) => e.preventDefault()} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('email')}
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: '10px' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('password')}
                  type="password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  InputProps={{ style: { borderRadius: '10px' } }}
                />
              </Grid>
              {errorMessage && (
                <Grid item xs={12}>
                  <Typography color="error" align="center">
                    {errorMessage}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    style={{ borderRadius: '10px', padding: '10px' }}
                    onClick={handleLogin}
                  >
                    {t('login_title')}
                  </Button>
                </motion.div>
              </Grid>
              <Grid item xs={12} textAlign="center">
                <Typography variant="body2">
                  <Link to="/forgot-password" style={{ textDecoration: 'none', color: '#1976d2' }}>
                    {t('forgot_password_link')}
                  </Link>
                </Typography>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </motion.div>
    </Container>
  );
}

export default Login;
